import { endOfDay, isWithinInterval, parse, startOfDay } from 'date-fns';
import { formatDateTime } from '../../../util/Helpers';
import { normalizeStoredFormSchema } from './form-io.util';

export const FORM_SUBMISSIONS_PAGE_SIZE = 25;

/**
 * Guard for `useParams().formId` before issuing submission API requests.
 * @param {unknown} formId
 * @returns {boolean}
 */
export const isFormIdPresent = (formId) => {
  if (formId == null) {
    return false;
  }
  if (typeof formId !== 'string' && typeof formId !== 'number') {
    return false;
  }
  return String(formId).trim() !== '';
};

/** Matches Feeds Audit `createdAt` column (`getBaseAuditColumns` in Audit.service.js) for DataGrid date-range filter. */
export const SUBMITTED_AT_DATE_FILTER_FORMAT = 'M/dd/yyyy';

/**
 * Row key holding ISO-ish `created_at` for client-side date-range filtering (not shown as a column).
 * @type {string}
 */
export const SUBMITTED_AT_RAW_ROW_KEY = 'submitted_at_raw';

const parseSubmittedRowDate = (raw) => {
  if (raw == null || raw === '') {
    return null;
  }
  if (typeof raw !== 'string' && typeof raw !== 'number') {
    return null;
  }
  const rowDate = new Date(String(raw));
  return Number.isNaN(rowDate.getTime()) ? null : rowDate;
};

const splitFilterDateParts = (filterValue) =>
  String(filterValue)
    .trim()
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const parseFilterDateRange = (parts) => {
  const start = parse(parts[0], SUBMITTED_AT_DATE_FILTER_FORMAT, new Date());
  const end =
    parts.length > 1
      ? parse(parts[1], SUBMITTED_AT_DATE_FILTER_FORMAT, new Date())
      : start;
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }
  return { start: startOfDay(start), end: endOfDay(end) };
};

/**
 * Client-side date range filter for Submitted column (DataGrid `paginationMode="client"`).
 * Filter value shape from DateRangeFilter: `start,end` formatted with {@link SUBMITTED_AT_DATE_FILTER_FORMAT}.
 * @param {string} _columnName
 * @param {string} filterValue
 * @param {{ rowData?: Record<string, unknown> }} data
 * @returns {typeof data | null}
 */
export const submittedAtDateRangeOnCustomFilter = (_columnName, filterValue, data) => {
  const rowDate = parseSubmittedRowDate(data.rowData?.[SUBMITTED_AT_RAW_ROW_KEY]);
  if (!rowDate) {
    return null;
  }
  const parts = splitFilterDateParts(filterValue);
  if (parts.length === 0) {
    return data;
  }
  const interval = parseFilterDateRange(parts);
  if (!interval) {
    return data;
  }
  return isWithinInterval(rowDate, interval) ? data : null;
};

/** Form.io submit-button rows are not useful in the grid. */
const EXCLUDED_SUBMISSION_DATA_KEYS = new Set(['submit']);

const baseColumn = (name, displayName, cellType = 'value') => ({
  name,
  displayName,
  sortOrder: 'none',
  isFilterable: true,
  isSortable: true,
  isVisible: true,
  type: cellType,
});

const buildSubmittedAtMetadataColumn = () => ({
  ...baseColumn('submitted_at', 'Submitted'),
  filter: {
    type: 'date-range',
    dateFormat: SUBMITTED_AT_DATE_FILTER_FORMAT,
    monthsShown: 2,
    onCustomFilterCallback: submittedAtDateRangeOnCustomFilter,
  },
});

/** DataGrid preset `LearnerCell` (same pattern as Feeds Audit user column). */
const buildUserMetadataColumn = () => ({
  name: 'user',
  displayName: 'User',
  sortOrder: 'none',
  isFilterable: true,
  isSortable: true,
  isVisible: true,
  type: 'preDefined',
  preset: 'LearnerCell',
  sortByProp: 'username',
  filter: {
    type: 'LearnerCell',
  },
});

const FACILITY_LOCATION_TYPE_ID = 3;
const HOUSING_UNIT_LOCATION_TYPE_ID = 15;

const compareByLocationName = (a, b) =>
  String(a.locationName).localeCompare(String(b.locationName));

const pickFacilitiesSorted = (locations) =>
  locations
    .filter((item) => item.locationTypeId === FACILITY_LOCATION_TYPE_ID)
    .sort(compareByLocationName);

const pickHousingUnitsForFacility = (locations, facility) =>
  locations
    .filter(
      (item) =>
        item.parentLocationId === facility.locationId &&
        item.locationTypeId === HOUSING_UNIT_LOCATION_TYPE_ID,
    )
    .sort(compareByLocationName);

const buildFacilityWithHousingUnits = (locations, facility) => [
  facility,
  ...pickHousingUnitsForFacility(locations, facility),
];

const toNonEmptyLocationName = (item) => {
  const name = item?.locationName;
  return name != null && String(name).trim() !== '' ? String(name) : null;
};

/**
 * Facility + housing-unit ordering, matching {@link getBaseAuditColumns} / Audit.service.js `getLocationOptions`.
 * @param {readonly { locationId?: number, locationName?: string, locationTypeId?: number, parentLocationId?: number }[]} locations
 * @returns {string[]}
 */
export const getLocationFilterOptions = (locations) => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }
  return pickFacilitiesSorted(locations)
    .flatMap((facility) => buildFacilityWithHousingUnits(locations, facility))
    .map(toNonEmptyLocationName)
    .filter(Boolean);
};

/**
 * MultiTagCell filter (frontend-commons) matches `preDefineComponentProps.location_name.tags[].label`.
 * @param {string} locationName
 * @returns {{ tags: { label: string }[] }}
 */
const locationTagsForMultiTagFilter = (locationName) => {
  const s = nonEmptyString(locationName);
  if (s === '') {
    return { tags: [] };
  }
  return { tags: [{ label: s }] };
};

/** Same shape as Audit hidden `location` column: multi-select via MultiTagCheckboxFilter. */
const buildLocationMetadataColumn = (locationOptions) => ({
  ...baseColumn('location_name', 'Location'),
  filter: {
    type: 'MultiTagCell',
    showBlankFilter: true,
  },
  options: Array.isArray(locationOptions) ? locationOptions : [],
});

const stringifySubmissionObject = (value) => {
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
};

const CELL_FORMATTERS = {
  boolean: (v) => (v ? 'Yes' : 'No'),
  string: String,
  number: String,
  object: stringifySubmissionObject,
};

/**
 * @param {unknown} value
 * @returns {string}
 */
export const formatSubmissionDataCell = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  const formatter = CELL_FORMATTERS[typeof value];
  return formatter ? formatter(value) : '';
};

/**
 * @param {string} value
 * @returns {string}
 */
const escapeHtmlAttr = (value) =>
  String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
    .replaceAll('<', '&lt;');

const SIGNATURE_URL_PREFIXES = ['http://', 'https://', 'data:'];

const extractSignatureUrlFromString = (raw) => {
  const trimmed = raw.trim();
  const hasAllowedPrefix = SIGNATURE_URL_PREFIXES.some((p) => trimmed.startsWith(p));
  return hasAllowedPrefix ? trimmed : null;
};

const extractSignatureUrlFromObject = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const url = /** @type {{ url?: unknown }} */ (raw).url;
  if (typeof url !== 'string' || url.trim() === '') {
    return null;
  }
  return url.trim();
};

/**
 * Form.io signature (and similar file) submission values: string data URL or `{ url }`.
 * @param {unknown} raw
 * @returns {string | null}
 */
export const extractSignatureOrFileUrl = (raw) => {
  if (typeof raw === 'string') {
    return extractSignatureUrlFromString(raw);
  }
  return extractSignatureUrlFromObject(raw);
};

export const formatSubmissionDataForCsv = (value, isSignatureField) => {
  if (isSignatureField) {
    const href = extractSignatureOrFileUrl(value);
    return href ?? '';
  }
  return formatSubmissionDataCell(value);
};

/**
 * @param {unknown} raw
 * @returns {string}
 */
export const buildSignatureViewFileHtml = (raw) => {
  const href = extractSignatureOrFileUrl(raw);
  if (!href) {
    return '';
  }
  return `<a href="${escapeHtmlAttr(href)}" target="_blank" rel="noopener noreferrer">View file</a>`;
};

const isSignatureNode = (node) =>
  node?.type === 'signature' &&
  typeof node.key === 'string' &&
  node.key.trim() !== '';

const addNodeKeyIfSignature = (node, keys) => {
  if (isSignatureNode(node)) {
    keys.add(node.key.trim());
  }
};

const visitNestedComponents = (node, visit) => {
  if (Array.isArray(node?.components)) {
    node.components.forEach(visit);
  }
};

const visitNestedColumns = (node, visit) => {
  if (!Array.isArray(node?.columns)) {
    return;
  }
  node.columns.forEach((col) => visitNestedComponents(col, visit));
};

const collectSignatureKeysFromNode = (node, keys) => {
  if (!node || typeof node !== 'object') {
    return;
  }
  addNodeKeyIfSignature(node, keys);
  const visit = (child) => collectSignatureKeysFromNode(child, keys);
  visitNestedComponents(node, visit);
  visitNestedColumns(node, visit);
};

/**
 * Collect `key`s for Form.io `signature` components from a stored form definition.
 * @param {unknown} definition
 * @returns {ReadonlySet<string>}
 */
export const extractSignatureKeysFromFormDefinition = (definition) => {
  /** @type {Set<string>} */
  const keys = new Set();
  const form = normalizeStoredFormSchema(definition?.schema);
  visitNestedComponents(form, (child) => collectSignatureKeysFromNode(child, keys));
  return keys;
};

/**
 * Sorted union of keys from `submission.data` objects across rows (Form.io field keys).
 * @param {readonly { data?: unknown }[]} submissions
 * @returns {string[]}
 */
export const collectSortedDataKeysFromSubmissions = (submissions) => {
  const keys = new Set();
  if (!Array.isArray(submissions)) {
    return [];
  }
  for (const row of submissions) {
    const raw = row?.data;
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      Object.keys(raw).forEach((k) => {
        if (!EXCLUDED_SUBMISSION_DATA_KEYS.has(k.toLowerCase())) {
          keys.add(k);
        }
      });
    }
  }
  return [...keys].sort((a, b) => a.localeCompare(b));
};

/**
 * @param {unknown} value
 * @returns {string}
 */
const nonEmptyString = (value) =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : '';

/**
 * Props for DataGrid `LearnerCell` from API `person` on a submission row.
 * @param {{ username?: string | null, firstName?: string | null, lastName?: string | null, docId?: string | null } | null | undefined} person
 */
const userCellPropsFromPerson = (person) => ({
  username: nonEmptyString(person?.username),
  firstName: nonEmptyString(person?.firstName),
  lastName: nonEmptyString(person?.lastName),
  docId: nonEmptyString(person?.docId),
});

/**
 * @param {{ id?: string, created_at?: string, reference_id?: string | null, data?: unknown, location_name?: string | null } | null | undefined} submission
 * @param {readonly string[]} dataKeys
 * @param {ReadonlySet<string>} signatureKeys
 * @returns {Record<string, string>}
 */
const buildSubmissionRowData = (submission, dataKeys, signatureKeys) => {
  const raw = submission?.data;
  const data =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const createdAt = submission?.created_at;
  const hasCreatedAt = createdAt != null && createdAt !== '';
  const rowData = {
    submitted_at: hasCreatedAt ? formatDateTime(createdAt) : '',
    [SUBMITTED_AT_RAW_ROW_KEY]: hasCreatedAt ? String(createdAt) : '',
    location_name: nonEmptyString(submission?.location_name),
  };
  for (const key of dataKeys) {
    const cell = data[key];
    rowData[key] = signatureKeys.has(key)
      ? buildSignatureViewFileHtml(cell)
      : formatSubmissionDataCell(cell);
  }
  return rowData;
};

/**
 * @param {readonly { id?: string, created_at?: string, reference_id?: string | null, data?: unknown }[]} submissions
 * @param {ReadonlySet<string>} signatureKeys
 * @param {readonly string[]} locationFilterOptions
 */
export const buildFormSubmissionsColumns = (submissions, signatureKeys, locationFilterOptions = []) => {
  const dataKeys = collectSortedDataKeysFromSubmissions(submissions);
  const meta = [
    buildUserMetadataColumn(),
    buildLocationMetadataColumn(locationFilterOptions),
    buildSubmittedAtMetadataColumn(),
  ];
  const dynamic = dataKeys.map((key) =>
    baseColumn(key, key, signatureKeys.has(key) ? 'html' : 'value'),
  );
  return [...meta, ...dynamic];
};

/**
 * @param {readonly { id?: string, created_at?: string, reference_id?: string | null, data?: unknown, location_name?: string | null, person?: { username?: string | null, firstName?: string | null, lastName?: string | null, docId?: string | null } }[]} submissions
 * @param {readonly string[]} dataKeys
 * @param {ReadonlySet<string>} signatureKeys
 */
export const mapFormSubmissionsToGridData = (submissions, dataKeys, signatureKeys) => {
  if (!Array.isArray(submissions)) {
    return [];
  }
  return submissions.map((submission) => ({
    isSelected: false,
    rowData: buildSubmissionRowData(submission, dataKeys, signatureKeys),
    preDefineComponentProps: {
      user: userCellPropsFromPerson(submission?.person),
      location_name: locationTagsForMultiTagFilter(submission?.location_name),
    },
  }));
};

const NAMED_COLUMN_STYLES = {
  submitted_at: { width: 200, minWidth: 200, maxWidth: 200 },
  user: {
    width: 360,
    minWidth: 280,
    maxWidth: 360,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  location_name: {
    minWidth: 160,
    maxWidth: 280,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

const HTML_COLUMN_STYLE = {
  minWidth: 100,
  maxWidth: 260,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

const DEFAULT_COLUMN_STYLE = {
  minWidth: 140,
  maxWidth: 260,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  wordBreak: 'normal',
};

const resolveColumnStyle = (col) => {
  if (NAMED_COLUMN_STYLES[col.name]) {
    return NAMED_COLUMN_STYLES[col.name];
  }
  return col.type === 'html' ? HTML_COLUMN_STYLE : DEFAULT_COLUMN_STYLE;
};

/**
 * Per-column cell styles for long JSON / ids.
 * @param {readonly { name: string, type?: string }[]} columns
 */
export const buildFormSubmissionsTdStyles = (columns) =>
  columns.reduce((styles, col) => {
    styles[col.name] = resolveColumnStyle(col);
    return styles;
  }, /** @type {Record<string, Record<string, unknown>>} */ ({}));
